/**
 * useCategoryScrollSync — IntersectionObserver-based tab <-> scroll sync.
 *
 * Observes every rendered `#cat-<id>` section inside `scrollContainerRef` and
 * keeps the `activeCategoryId` in `catalogueSlice` up to date as the user
 * scrolls. Calling `scrollToCategory(id)` suspends the observer briefly so a
 * click-triggered smooth-scroll doesn't flicker the tabs while the viewport
 * is in flight.
 *
 * Based on the archived pattern from `CatalogTabs.component.reference.tsx`
 * (seed-13), generalized to accept a dynamic category list.
 *
 * Scroll-spy strategy:
 *   A single IntersectionObserver watches all sections with a root margin that
 *   creates a narrow trigger band near the top of the scroll container
 *   (-10% from top, -85% from bottom → roughly the top 5% of the viewport).
 *   When a section's leading edge crosses that band it fires `isIntersecting`,
 *   becoming the new active category. This prevents false positives from tall
 *   sections that remain partially visible long after the user has scrolled
 *   into the next one.
 *
 *   When multiple entries fire in the same batch (e.g. fast scroll), we pick
 *   the topmost intersecting section (smallest non-negative boundingClientRect.top
 *   relative to the root) to match reading order.
 */

import { useCallback, useEffect, useRef } from 'react'

import { usePfStore } from '../../store'

/** Scroll offset still counted as the top of the page. */
const TOP_OF_PAGE_PX = 8

interface Options {
  /** Scroll container; sections must be nested under it. */
  scrollContainerRef: React.RefObject<HTMLElement | null>
  /** Category ids currently rendered (order-independent — IO handles it). */
  categoryIds: string[]
  /** Prefix for section DOM ids. Defaults to 'cat-'. */
  domIdPrefix?: string
}

export interface CategoryScrollSync {
  scrollToCategory: (categoryId: string) => void
}

export function useCategoryScrollSync({
  scrollContainerRef,
  categoryIds,
  domIdPrefix = 'cat-',
}: Options): CategoryScrollSync {
  const setActiveCategory = usePfStore((s) => s.setActiveCategory)
  /** Suppresses observer-driven updates during a click-triggered smooth scroll. */
  const isProgrammaticScroll = useRef(false)
  /** Latest stable setter reference captured once — Zustand actions are stable. */
  const setActiveCategoryRef = useRef(setActiveCategory)
  setActiveCategoryRef.current = setActiveCategory

  const scrollToCategory = useCallback(
    (categoryId: string) => {
      const container = scrollContainerRef.current
      if (!container) return

      const el = document.getElementById(`${domIdPrefix}${categoryId}`)
      if (!el) return

      isProgrammaticScroll.current = true
      setActiveCategoryRef.current(categoryId)
      el.scrollIntoView({ behavior: 'smooth', block: 'start' })

      // Keep the flag up for ~800ms to let smooth-scroll finish.
      window.setTimeout(() => {
        isProgrammaticScroll.current = false
      }, 800)
    },
    [domIdPrefix, scrollContainerRef]
  )

  useEffect(() => {
    const container = scrollContainerRef.current
    if (!container || categoryIds.length === 0) return

    // Build an id → categoryId lookup so we can resolve entries back to ids
    // without closing over the loop variable.
    const elToId = new Map<Element, string>()
    const targets: Element[] = []

    for (const id of categoryIds) {
      const el = document.getElementById(`${domIdPrefix}${id}`)
      if (!el) continue
      elToId.set(el, id)
      targets.push(el)
    }

    if (targets.length === 0) return

    /**
     * rootMargin creates a trigger band: clip 10% from the top and 85% from
     * the bottom of the scroll container. Only the ~5% band near the top of
     * the visible area can trigger an intersection.  When the top edge of a
     * section enters this band the section becomes active; when it exits
     * downward (user scrolls back up) the previous section takes over because
     * its bottom edge re-enters the band.
     */
    const observer = new IntersectionObserver(
      (entries) => {
        if (isProgrammaticScroll.current) return

        // Among all currently-intersecting entries, pick the topmost one
        // (the section whose top is closest to — but inside — the root's
        // visible area). This handles fast scroll batches correctly.
        const intersecting = entries.filter((e) => e.isIntersecting)
        if (intersecting.length === 0) return

        const topmost = intersecting.reduce((best, current) =>
          current.boundingClientRect.top < best.boundingClientRect.top ? current : best
        )

        const id = elToId.get(topmost.target)
        if (id) setActiveCategoryRef.current(id)
      },
      {
        // The catalogue uses window scroll (the scrollContainerRef Box has no
        // overflow of its own). Passing the wrapper as `root` would never fire
        // because the wrapper itself never scrolls. `root: null` tracks the
        // viewport instead.
        root: null,
        // Trigger band ≈ top 15% of viewport: a section becomes active when
        // its leading edge crosses just below the sticky tab strip.
        rootMargin: '-10% 0px -85% 0px',
        threshold: 0,
      }
    )

    for (const el of targets) observer.observe(el)

    /**
     * Approach A: page-bottom detection for the last category.
     *
     * When the last section is shorter than the observer's trigger band, its
     * leading edge may never fire `isIntersecting` before the user hits the
     * bottom. We catch this by listening to the scroll event: once the user
     * is within 4px of the document bottom, force-activate the last category.
     */
    // At the very top no section has crossed the trigger band yet, so the first one is the one in view.
    const activateFirstAtTop = () => {
      if (window.scrollY > TOP_OF_PAGE_PX) return false
      setActiveCategoryRef.current(categoryIds[0])
      return true
    }

    const handleScroll = () => {
      if (isProgrammaticScroll.current) return
      if (activateFirstAtTop()) return
      const atBottom =
        window.innerHeight + window.scrollY >= document.body.offsetHeight - 4
      if (atBottom) {
        const lastId = categoryIds[categoryIds.length - 1]
        if (lastId) setActiveCategoryRef.current(lastId)
      }
    }

    window.addEventListener('scroll', handleScroll, { passive: true })
    activateFirstAtTop()

    return () => {
      observer.disconnect()
      window.removeEventListener('scroll', handleScroll)
    }
  }, [categoryIds, domIdPrefix, scrollContainerRef])

  return { scrollToCategory }
}
